import {
  fetchCounsilors,
  modifyProject,
  updateCounsilorImage,
} from "api/counsilors";
import { uploadImageToS3 } from "api/news";
import SubmitProjectArticle from "components/Modal/SubmitProjectArticle";
import { setCurrentPageNo } from "pages/ArticlesApprovalManagement/articlesSlice";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import style from "../CreateNotification/create_noti.module.scss";
import Header from "./components/header";
import { base64ToBlob } from "./components/helper";
import LeftSection from "./components/leftSection";
import RightSection from "./components/rightSection";

function CreateArticle({
  setOpen,
  setTabActive = () => {},
  myNewsArticle,
  isEdit,
  handleTabChange = () => {},
  selectedArticle,
  setNews = () => {},
  setDetails = () => {},
  setLoadTable = () => {},
  setArticlessData = () => {},
}) {
  const dispatch = useDispatch();

  const [title, setTitle] = useState(isEdit ? selectedArticle?.title : "");
  const [message, setMessage] = useState(
    isEdit ? selectedArticle?.project_content : ""
  );
  const [abstract, setAbstract] = useState(
    isEdit ? selectedArticle?.description : ""
  );
  const [submit, setSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadingPublish, setLoadingPublish] = useState(false);

  // ✅ FIXED: Use correct key spelling for councillor ID
  const [counsilor, setCounsilor] = useState(() => {
    const key = selectedArticle?.councilor_id; // ✅ correct spelling
    const first = selectedArticle?.first_name || "";
    const last = selectedArticle?.last_name || "";
    const label = `${first} ${last}`.trim();

    return {
      counsilor_data: key
        ? { key, label: label || "" }
        : { key: null, label: "" },
      ward_data: selectedArticle?.ward_code || null,
      startDate: "",
      endDate: "",
      reason: "",
    };
  });

  const [newsImage, setNewsImage] = useState(
    isEdit ? selectedArticle?.images?.[0]?.project_image : null
  );

  const [searchInput, setSearchInput] = useState("");
  const [counsilorData, setCounsilorData] = useState([]);
  const [loadingCounsilor, setLoadingCounsilor] = useState(false);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchCouncillors();
    }, 300);
    return () => clearTimeout(delay);
  }, [searchInput]);

  const fetchCouncillors = async () => {
    setLoadingCounsilor(true);
    try {
      const params = {
        page: 1,
        perPage: 100,
        ...(searchInput ? { councilorName: searchInput } : { limit: 100 }),
        status: 1,
      };

      const response = await fetchCounsilors(params);
      if (response?.councilors) {
        const validCouncillors = response.councilors.filter(
          (c) => c?.ward_code
        );

        const formatted = validCouncillors.map((c) => ({
          key: c.councilor_id,
          label: `${c.first_name} ${c.last_name}`,
          ...c,
        }));

        setCounsilorData(formatted);

        if (
          isEdit &&
          selectedArticle &&
          !counsilor?.counsilor_data?.label &&
          counsilor?.counsilor_data?.key
        ) {
          const match = formatted.find(
            (c) => c.key === selectedArticle.councilor_id
          );
          if (match) {
            setCounsilor((prev) => ({
              ...prev,
              counsilor_data: {
                key: match.key,
                label: match.label,
              },
              ward_data: match.ward_code,
            }));
          }
        }
      }
    } catch (err) {
    } finally {
      setLoadingCounsilor(false);
    }
  };

  const checkValidation = () => {
    if (
      !title.trim() ||
      !message.trim() ||
      !abstract.trim() ||
      !newsImage ||
      !counsilor?.counsilor_data?.key
    ) {
      toast.warn(
        "All fields including title, content, description, councillor and an image are required."
      );
    } else {
      setSubmit(true);
    }
  };

  const checkDraftValidation = () => {
    if (!title.trim() || !message.trim() || !counsilor?.counsilor_data?.key) {
      toast.warn(
        "A title, content, and councillor are required to save as a draft."
      );
    } else {
      handleArticleSubmit("draft");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setNews(false);
    setDetails(false);
  };

  const handleArticleSubmit = async (type) => {
    if (type === "draft") setLoadingDraft(true);
    else setLoadingPublish(true);

    const formData = {
      action: isEdit ? "update" : "create",
      councilorId: counsilor?.counsilor_data?.key,
      wardCode: counsilor?.ward_data,
      title,
      description: abstract,
      content: message,
      objectCount: 1,
      status: type === "draft" ? 0 : 1,
    };

    if (isEdit) {
      formData.councilorProjectId = selectedArticle?.councilor_project_id;
    }

    try {
      const response = await modifyProject(formData);

      if (response) {
        const updateObj = {};
        let imageAction = null;
        const imageData = selectedArticle?.images?.[0];
        const projectId = response?.data?.councilor_project_id;

        if (
          response?.data?.project_image_upload_path &&
          newsImage &&
          !newsImage.startsWith("https://")
        ) {
          const newsImageBlob = base64ToBlob(newsImage);
          await uploadImageToS3(
            newsImageBlob,
            response?.data?.project_image_upload_path
          );

          updateObj.councilor_image_name = "articleImage";
          updateObj.councilor_image_size = newsImageBlob?.size;
          updateObj.councilor_image_mimetype = newsImageBlob?.type;
          updateObj.councilor_image_filepath =
            response?.data?.project_image_upload_filepath;

          if (isEdit && imageData?.project_image_id) {
            updateObj.councilor_image_id = imageData?.project_image_id;
            imageAction = "update";
          } else {
            imageAction = "insert";
          }
        } else if (
          type === "draft" &&
          !newsImage &&
          isEdit &&
          imageData?.project_image_id
        ) {
          updateObj.councilor_image_id = imageData.project_image_id;
          updateObj.councilor_image_name = imageData.project_image_name;
          updateObj.councilor_image_size = imageData.project_image_size;
          updateObj.councilor_image_mimetype = imageData.project_image_mimetype;
          updateObj.councilor_image_filepath = imageData.project_image_filepath;
          imageAction = "delete";
        }

        if (Object.keys(updateObj)?.length > 0 && imageAction) {
          const imagePayload = {
            councilor_id: counsilor?.counsilor_data?.key,
            action: imageAction,
            type: 2,
            images: [updateObj],
          };

          if (imageAction !== "update") {
            imagePayload.councilor_project_id = projectId;
          }

          await updateCounsilorImage(imagePayload);
        }

        toast.success(response?.message || "Article submitted successfully");

        dispatch(setCurrentPageNo(1));
        setArticlessData([]);
        setLoadTable((prev) => !prev);
        handleTabChange(type === "draft" ? "4" : "2");
        setTabActive(type === "draft" ? "4" : "2");
        handleClose();
      }

      setSubmit(false);
      setLoading(false);
      setDetails(false);
    } catch (error) {
      setSubmit(false);
      handleClose();
      setLoading(false);
    } finally {
      handleTabChange(type === "draft" ? "4" : "2");
      setTabActive(type === "draft" ? "4" : "2");
      setArticlessData([]);
      if (type === "draft") {
        setLoadingDraft(false);
      } else {
        setLoadingPublish(false);
      }
    }
  };

  const handleImageUpdate = async (value, type, file) => {
    const imagePath =
      type === "news"
        ? selectedArticle?.news_image_filepath
        : selectedArticle?.author_image_path;

    if (isEdit && imagePath) {
      try {
        const imageData = {
          news_image_name: file?.name,
          news_image_size: file.size || 5000,
          news_image_mimetype: file.type || "image/jpeg",
          news_image_filepath: imagePath,
          image_type: type === "news" ? 1 : 2,
        };

        const response = await updateCounsilorImage({
          news_id: Number(selectedArticle?.id),
          is_insert: value,
          images: [imageData],
        });

        toast.success(response?.message);
      } catch (error) {}
    }
  };

  return (
    <>
      <div className="w-full create">
        <Header
          loadingDraft={loadingDraft}
          loadingPublish={loadingPublish}
          handleClose={handleClose}
          isEdit={isEdit}
          myNewsArticle={myNewsArticle}
          isLoading={loading}
          handleTabChange={handleTabChange}
          checkDraftValidation={checkDraftValidation}
          checkValidation={checkValidation}
        />
        <div className={`flex ${style["child-wrapper"]}`}>
          <LeftSection
            isEdit={isEdit}
            selectedArticle={selectedArticle}
            counsilor={counsilor}
            setCounsilor={setCounsilor}
            handleImageUpdate={handleImageUpdate}
            setNewsImage={setNewsImage}
            newsImage={newsImage}
            counsilorData={counsilorData}
            loading={loadingCounsilor}
            setSearchInput={setSearchInput}
          />
          <RightSection
            setTitle={setTitle}
            title={title}
            message={message}
            setMessage={setMessage}
            abstract={abstract}
            setAbstract={setAbstract}
          />
        </div>
      </div>

      <SubmitProjectArticle
        title={title}
        loading={loadingPublish}
        onSubmit={handleArticleSubmit}
        open={submit}
        setOpen={setSubmit}
      />
    </>
  );
}

export default CreateArticle;
